import { useState, useEffect, useRef } from "react";
import { generateSensorikPdf } from "@/lib/generatePdf";

interface Criterion {
  id: string;
  label: string;
  score: number;
  max: number;
  percent: number;
  desc: string;
}

interface Dimension {
  label: string;
  icon: string;
  score: number;
  max: number;
  percent: number;
  criteria: Criterion[];
}

interface Optimierung {
  dimension: string;
  kriterium: string;
  score: number;
  max: number;
  prioritaet: "hoch" | "mittel";
  tipp: string;
}

interface Reifegrad {
  name: string;
  desc: string;
  min: number;
  max: number;
}

const BRANCHEN_HINWEISE: Record<string, { label: string; hinweis: string }> = {
  dienstleister: {
    label: "Dienstleister / Beratung",
    hinweis: "Für Dienstleister sind Vertrauen, Storytelling und emotionale Sprache besonders entscheidend. Potenzielle Kunden kaufen zunächst die Person, dann die Leistung. Ein hoher Score in 'Assoziative Sensorik' und 'Sprachliche Sensorik' ist hier wichtiger als in anderen Branchen.",
  },
  ecommerce: {
    label: "E-Commerce / Online-Shop",
    hinweis: "Im E-Commerce sind Bildqualität, CTA-Qualität und Interaktivität die wichtigsten Hebel. Produktbilder sollten Emotionen auslösen, nicht nur informieren. Ein niedriger Score in 'Visuelle Sensorik' oder 'Haptisch-Kinetische Sensorik' kostet Sie direkt Umsatz.",
  },
  gastronomie: {
    label: "Gastronomie / Hotel / Tourismus",
    hinweis: "In dieser Branche müssen alle Sinne angesprochen werden – Bilder, die man fast schmecken kann, Texte, die Atmosphäre vermitteln, und Videos, die Vorfreude auslösen. Alle fünf Dimensionen sind hier gleich wichtig. Ein Score unter 60 bedeutet: Ihre Website verkauft das Erlebnis nicht.",
  },
  gesundheit: {
    label: "Gesundheit / Wellness / Beauty",
    hinweis: "Vertrauen ist in dieser Branche das wichtigste Kapital. Zertifikate, Bewertungen und eine beruhigende, klare Bildsprache sind entscheidend. Achten Sie besonders auf Ihren Score in 'Assoziative Sensorik' (Vertrauenssignale) und 'Visuelle Sensorik' (Weißraum & Ruhe).",
  },
  b2b: {
    label: "B2B / Software / Technologie",
    hinweis: "B2B-Websites neigen zu sachlicher, rationaler Kommunikation – das ist bewusst so. Dennoch werden auch B2B-Kaufentscheidungen emotional beeinflusst. Ein mittlerer Score (40–60) kann hier völlig ausreichend sein, solange 'Assoziative Sensorik' (Vertrauen, Referenzen) stark ist.",
  },
  handwerk: {
    label: "Handwerk / Bau / Immobilien",
    hinweis: "Handwerksbetriebe profitieren enorm von Vorher-Nachher-Bildern, Kundenstimmen und einer klaren, vertrauenswürdigen Sprache. Ihr größter Hebel liegt meist in 'Sprachliche Sensorik' und 'Assoziative Sensorik' – hier ist der Abstand zur Konkurrenz am größten.",
  },
  kreativ: {
    label: "Kreativbranche / Agentur / Design",
    hinweis: "Als Kreativunternehmen ist Ihre Website Ihr wichtigstes Portfolio-Stück. Ein niedriger Sensorik-Score ist hier besonders schädlich, weil er direkt Ihre Kompetenz in Frage stellt. Alle fünf Dimensionen sollten überdurchschnittlich stark sein.",
  },
};

interface PageCrawled {
  url: string;
  type: string;
  title: string;
  error: string | null;
}

interface AnalysisData {
  url: string;
  page_title: string;
  branche?: string;
  total_score: number;
  scores: Record<string, Dimension>;
  reifegrad: Reifegrad;
  optimierungen: Optimierung[];
  screenshot_b64: string | null;
  pages_crawled?: PageCrawled[];
  details: {
    word_count: number;
    image_count: number;
    has_video: boolean;
    has_audio: boolean;
    has_animations: boolean;
    cta_count: number;
    has_viewport_meta: boolean;
    sensorische_woerter_count: number;
    emotionale_woerter_count: number;
  };
}

interface Props {
  data: Record<string, unknown>;
  onReset: () => void;
}

export default function Results({ data, onReset }: Props) {
  const d = data as unknown as AnalysisData;
  const [animatedScore, setAnimatedScore] = useState(0);
  const [showDetails, setShowDetails] = useState<string | null>(null);
  const [expandedTip, setExpandedTip] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const scoreRef = useRef(0);

  const handlePdfDownload = async () => {
    setPdfLoading(true);
    try {
      generateSensorikPdf(data as Record<string, unknown>);
    } catch (err) {
      console.error("[PDF-Fehler]", err);
      alert("PDF konnte nicht erstellt werden: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setTimeout(() => setPdfLoading(false), 1500);
    }
  };

  // Animate score counter
  useEffect(() => {
    const target = d.total_score;
    const duration = 1500;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);
      setAnimatedScore(current);
      scoreRef.current = current;
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [d.total_score]);

  const needleAngle = -90 + (d.total_score / 100) * 180;

  const getScoreColor = (score: number) => {
    if (score < 40) return "#EF4444";
    if (score < 70) return "#F59E0B";
    return "#22C55E";
  };

  const scoreColor = getScoreColor(d.total_score);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <img src="/manus-storage/logo_2d439bd1.jpg" alt="WERBEAGENTUR Larissa Girenko" className="w-10 h-10 object-contain" />
          <span className="text-black font-black text-sm tracking-widest uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Sensorik-Barometer™</span>
        </div>
        <button
          onClick={onReset}
          className="text-gray-500 hover:text-black text-xs font-bold uppercase tracking-widest border border-gray-200 hover:border-gray-400 px-4 py-2 transition-colors"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          ← Neue Analyse
        </button>
      </header>

      {/* URL Banner */}
      <div className="bg-[#f2f2f2] border-b border-gray-200 px-6 py-3 flex items-center gap-3 flex-wrap">
        <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
        <span className="text-gray-500 text-xs font-medium uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>Analysiert:</span>
        <span className="text-black text-sm font-medium truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.url}</span>
        {d.page_title && (
          <span className="text-gray-400 text-xs hidden md:block" style={{ fontFamily: "'DM Sans', sans-serif" }}>· {d.page_title}</span>
        )}
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          {d.pages_crawled && d.pages_crawled.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-gray-400 text-xs uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>Seiten:</span>
              {d.pages_crawled.map((pg: PageCrawled, i: number) => (
                <span key={i} className="text-xs px-2 py-0.5 border border-gray-300 text-gray-500 bg-white" title={pg.url} style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {pg.type === 'startseite' ? '🏠' : pg.type === 'kategorie' ? '📂' : '📦'} {pg.type}
                </span>
              ))}
            </div>
          )}
          {/* PDF-Download-Button */}
          <button
            onClick={handlePdfDownload}
            disabled={pdfLoading}
            className="flex items-center gap-2 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#6d0000', fontFamily: "'DM Sans', sans-serif" }}
            onMouseEnter={(e) => !pdfLoading && (e.currentTarget.style.backgroundColor = '#0d4440')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#115751')}
          >
            {pdfLoading ? (
              <><svg className="animate-spin w-3 h-3 inline-block mr-1" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Wird erstellt …</>
            ) : (
              <>PDF herunterladen</>
            )}
          </button>
        </div>
      </div>

      <div className="container py-8 max-w-6xl">
        {/* ── Score Hero ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Barometer */}
          <div className="md:col-span-1 bg-black p-8 flex flex-col items-center">
            <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sensorik-Score</p>

            {/* SVG Barometer */}
            <div className="relative w-56 h-32 mb-4">
              <svg viewBox="0 0 200 110" className="w-full h-full">
                {/* Background arc */}
                <path d="M 10 100 A 90 90 0 0 1 190 100" fill="none" stroke="#2A2A2A" strokeWidth="12" strokeLinecap="round"/>
                {/* Color zones */}
                <path d="M 10 100 A 90 90 0 0 1 55 28" fill="none" stroke="#6C0000" strokeWidth="12" strokeLinecap="round" opacity="0.6"/>
                <path d="M 55 28 A 90 90 0 0 1 145 28" fill="none" stroke="#B8860B" strokeWidth="12" strokeLinecap="round" opacity="0.6"/>
                <path d="M 145 28 A 90 90 0 0 1 190 100" fill="none" stroke="#166534" strokeWidth="12" strokeLinecap="round" opacity="0.6"/>
                {/* Needle */}
                <g transform={`translate(100, 100) rotate(${needleAngle})`} style={{ transition: "transform 1.5s cubic-bezier(0.34, 1.56, 0.64, 1)" }}>
                  <line x1="0" y1="0" x2="0" y2="-75" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                  <circle cx="0" cy="0" r="5" fill="white"/>
                </g>
                {/* Labels */}
                <text x="8" y="115" fill="#6C0000" fontSize="9" fontWeight="bold" fontFamily="Montserrat">0</text>
                <text x="93" y="18" fill="white" fontSize="9" fontWeight="bold" fontFamily="Montserrat" textAnchor="middle">50</text>
                <text x="186" y="115" fill="#22C55E" fontSize="9" fontWeight="bold" fontFamily="Montserrat" textAnchor="end">100</text>
              </svg>
            </div>

            {/* Score number */}
            <div className="text-center">
              <div className="font-black text-6xl leading-none" style={{ color: scoreColor }}>
                {animatedScore}
              </div>
              <div className="text-white/40 text-sm font-medium mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>von 100 Punkten</div>
            </div>
          </div>

          {/* Reifegrad */}
          <div className="md:col-span-2 bg-[#f2f2f2] border border-gray-200 p-8 flex flex-col justify-between">
            <div>
              <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sensorischer Reifegrad</p>
              <h2 className="font-bold text-3xl uppercase tracking-tight mb-4" style={{ color: scoreColor, fontFamily: "'Roboto', sans-serif" }}>
                {d.reifegrad.name}
              </h2>
              <p className="text-gray-600 text-base leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.reifegrad.desc}</p>
            </div>

            {/* Branchen-Hinweis */}
            {d.branche && BRANCHEN_HINWEISE[d.branche] && (
              <div className="mt-5 border-l-4 pl-4 py-2" style={{ borderColor: '#6d0000', backgroundColor: '#6d000008' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#6d0000', fontFamily: "'DM Sans', sans-serif" }}>
                  Kontext: {BRANCHEN_HINWEISE[d.branche].label}
                </p>
                <p className="text-gray-600 text-xs leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {BRANCHEN_HINWEISE[d.branche].hinweis}
                </p>
              </div>
            )}
            {!d.branche && (
              <div className="mt-5 border-l-4 pl-4 py-2 border-gray-300 bg-gray-50">
                <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Hinweis zur Interpretation</p>
                <p className="text-gray-400 text-xs leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Dieser Score ist ein branchenunabhängiger Basiswert. Für eine kontextbezogene Interpretation empfehlen wir ein persönliches Strategie-Gespräch.
                </p>
              </div>
            )}

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
              {[
                { label: "Wörter", value: d.details.word_count, icon: "/manus-storage/icon_content_lesbarkeit_9425589a.png" },
                { label: "Bilder", value: d.details.image_count, icon: "/manus-storage/11_094992a3.svg" },
                { label: "CTAs", value: d.details.cta_count, icon: "/manus-storage/icon_cta_value_9a48b0a2.png" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <img src={stat.icon} alt="" className="w-5 h-5 mb-1 object-contain" style={{ opacity: 0.85 }} />
                  <div className="text-black font-black text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{stat.value}</div>
                  <div className="text-gray-400 text-xs uppercase tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Screenshot ── */}
        {d.screenshot_b64 && (
          <div className="mb-8 bg-[#f2f2f2] border border-gray-200 p-4">
            <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Website-Screenshot</p>
            <img
              src={`data:image/jpeg;base64,${d.screenshot_b64}`}
              alt="Website Screenshot"
              className="w-full border border-white/10"
              style={{ maxHeight: "300px", objectFit: "cover", objectPosition: "top" }}
            />
          </div>
        )}

        {/* ── Dimensionen ── */}
        <div className="mb-8">
          <h3 className="font-bold text-xl uppercase tracking-tight mb-4 text-black" style={{ fontFamily: "'Roboto', sans-serif" }}>
            Analyse nach Dimensionen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(d.scores).map(([key, dim]) => (
              <div key={key} className="bg-white border border-[#0D0D0D]/10">
                {/* Dimension Header */}
                <div className="bg-black px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src={dim.icon} alt="" className="w-6 h-6 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <span className="text-white font-bold text-sm uppercase tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dim.label}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-black text-lg" style={{ color: getScoreColor(dim.percent) }}>
                      {dim.score}
                    </span>
                    <span className="text-white/30 text-sm">/{dim.max}</span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="h-1.5 bg-gray-200">
                  <div
                    className="h-full transition-all duration-1000"
                    style={{
                      width: `${dim.percent}%`,
                      backgroundColor: getScoreColor(dim.percent),
                    }}
                  />
                </div>

                {/* Criteria */}
                <div className="p-4">
                  {dim.criteria.map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-2 border-b border-[#0D0D0D]/5 last:border-0">
                      <div className="flex-1 min-w-0">
                        <span className="text-[#0D0D0D] text-xs font-semibold">{c.label}</span>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <div className="w-16 h-1 bg-[#F0F0F0] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: `${c.percent}%`,
                              backgroundColor: getScoreColor(c.percent),
                            }}
                          />
                        </div>
                        <span className="text-[#0D0D0D] text-xs font-black w-8 text-right">
                          {c.score}/{c.max}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Toggle details */}
                <button
                  onClick={() => setShowDetails(showDetails === key ? null : key)}
                  className="w-full text-xs font-bold uppercase tracking-widest py-2 border-t border-gray-200 transition-colors"
                  style={{ color: '#6d0000', fontFamily: "'DM Sans', sans-serif" }}
                >
                  {showDetails === key ? "▲ Weniger" : "▼ Details"}
                </button>

                {showDetails === key && (
                  <div className="px-4 pb-4 bg-[#F8F6F4]">
                    {dim.criteria.map((c) => (
                      <div key={c.id} className="py-2 border-b border-[#0D0D0D]/5 last:border-0">
                        <p className="text-[#0D0D0D]/50 text-xs">{c.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Erkannte Merkmale ── */}
        <div className="mb-8 bg-[#f2f2f2] border border-gray-200 p-6">
          <h3 className="text-black font-bold text-lg uppercase tracking-tight mb-4" style={{ fontFamily: "'Roboto', sans-serif" }}>
            Erkannte Merkmale
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Video-Inhalte", value: d.details.has_video, icon: "/manus-storage/11_094992a3.svg" },
              { label: "Audio-Inhalte", value: d.details.has_audio, icon: "/manus-storage/11_094992a3.svg" },
              { label: "Animationen", value: d.details.has_animations, icon: "/manus-storage/7_8bcad2d2.svg" },
              { label: "Mobile-optimiert", value: d.details.has_viewport_meta, icon: "/manus-storage/33_d51b7819.svg" },
            ].map((item) => (
              <div key={item.label} className={`flex items-center gap-3 p-3 border ${item.value ? "border-green-300 bg-green-50" : "border-gray-200 bg-white"}`}>
                <img src={item.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                <div>
                  <div className={`text-xs font-bold uppercase tracking-wide ${item.value ? "text-green-600" : "text-gray-400"}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {item.value ? "✓ Vorhanden" : "✗ Fehlt"}
                  </div>
                  <div className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.label}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="flex items-center gap-3 p-3 border border-gray-200 bg-white">
              <img src="/manus-storage/icon_onpage_grundlagen_a1ddd99a.png" alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
              <div>
                <div className="text-black font-black text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{d.details.sensorische_woerter_count}</div>
                <div className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sensorische Wörter gefunden</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 border border-gray-200 bg-white">
              <img src="/manus-storage/icon_onpage_grundlagen_a1ddd99a.png" alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
              <div>
                <div className="text-black font-black text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{d.details.emotionale_woerter_count}</div>
                <div className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Emotionale Wörter gefunden</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Optimierungsempfehlungen ── */}
        {d.optimierungen.length > 0 && (
          <div className="mb-8">
            <h3 className="font-bold text-xl uppercase tracking-tight mb-2 text-black" style={{ fontFamily: "'Roboto', sans-serif" }}>
              Priorisierte Optimierungsempfehlungen
            </h3>
            <p className="text-gray-400 text-sm mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Sortiert nach Handlungsbedarf – die wichtigsten Maßnahmen zuerst
            </p>
            <div className="space-y-3">
              {d.optimierungen.map((opt, i) => (
                <div
                  key={i}
                  className={`border-l-4 bg-white border border-gray-200 ${opt.prioritaet === "hoch" ? "border-l-[#6d0000]" : "border-l-[#b45309]"}`}
                >
                  <button
                    className="w-full text-left px-5 py-4 flex items-center justify-between"
                    onClick={() => setExpandedTip(expandedTip === i ? null : i)}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 flex-shrink-0`} style={{ backgroundColor: opt.prioritaet === 'hoch' ? '#6d000015' : '#b4530915', color: opt.prioritaet === 'hoch' ? '#6d0000' : '#b45309', fontFamily: "'DM Sans', sans-serif" }}>
                        {opt.prioritaet === "hoch" ? "⚡ Hoch" : "○ Mittel"}
                      </span>
                      <div className="min-w-0">
                        <div className="text-black font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{opt.kriterium}</div>
                        <div className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{opt.dimension}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                      <div className="text-right">
                        <span className="font-black text-sm" style={{ color: getScoreColor((opt.score / opt.max) * 100) }}>
                          {opt.score}/{opt.max}
                        </span>
                      </div>
                      <span className="text-[#0D0D0D]/30 text-xs">{expandedTip === i ? "▲" : "▼"}</span>
                    </div>
                  </button>
                  {expandedTip === i && (
                    <div className="px-5 pb-4 border-t border-gray-100">
                      <p className="text-gray-600 text-sm leading-relaxed mt-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>{opt.tipp}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CTA ── */}
        <div className="p-8 text-center" style={{ backgroundColor: '#6d0000' }}>
          <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nächster Schritt</p>
          <h3 className="text-white font-bold text-2xl uppercase tracking-tight mb-3" style={{ fontFamily: "'Roboto', sans-serif" }}>
            Ihr Score hat Potenzial
          </h3>
          <p className="text-white/80 text-base mb-6 max-w-lg mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Larissa analysiert Ihren Sensorik-Score persönlich und zeigt Ihnen konkret, wie Sie mehr Kunden gewinnen können.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handlePdfDownload}
              disabled={pdfLoading}
              className="text-white font-bold text-sm uppercase tracking-widest px-8 py-3 transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#6d0000', fontFamily: "'DM Sans', sans-serif" }}
            >
              {pdfLoading ? '⏳ Wird erstellt …' : '📄 PDF-Report herunterladen'}
            </button>
            <a
              href="https://werbeagentur.me"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white font-bold text-sm uppercase tracking-widest px-8 py-3 hover:bg-gray-100 transition-colors"
              style={{ color: '#6d0000', fontFamily: "'DM Sans', sans-serif" }}
            >
              Strategie-Call buchen →
            </a>
            <button
              onClick={onReset}
              className="border border-white/40 text-white font-bold text-sm uppercase tracking-widest px-8 py-3 hover:bg-white/10 transition-colors"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Neue URL analysieren
            </button>
          </div>
        </div>
      </div>

      <footer className="bg-white border-t border-gray-200 px-6 py-4 text-center">
        <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          © 2026 WERBEAGENTUR Larissa Girenko · Sensorik-Barometer™ · Dein Schlüssel zum Marketingerfolg
        </p>
      </footer>
    </div>
  );
}
